import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { ColumnDef } from '@tanstack/react-table'
import { ExternalLink, Plus } from 'lucide-react'
import { apiFetch, qs } from '../lib/api'
import type { CouponRow } from '../lib/types'
import { DataTable } from '../components/data-table'
import { CursorPagination } from '../components/pagination'
import { PageHeader } from '../components/page'
import { ErrorState, Spinner } from '../components/states'
import { StatusBadge } from '../components/badge'

interface CouponList {
	rows: CouponRow[]
	nextCursor: string | null
}

function discountOf(c: CouponRow): string {
	if (c.discountType === 'percentage') return `${c.discountPercentage ?? 0}%`
	if (c.discountAmount === null) return '—'
	return `${(c.discountAmount / 100).toFixed(2)} ${c.currencyCode ?? ''}`.trim()
}

const columns: ColumnDef<CouponRow, any>[] = [
	{
		header: 'Code',
		accessorKey: 'id',
		cell: ({ row }) => (
			<div className="min-w-0">
				<div className="truncate font-medium">{row.original.id}</div>
				<div className="truncate text-xs text-ink-soft">{row.original.name}</div>
			</div>
		),
	},
	{ header: 'Discount', cell: ({ row }) => discountOf(row.original) },
	{ header: 'Duration', accessorKey: 'durationType', cell: ({ getValue }) => String(getValue()).replace(/_/g, ' ') },
	{ header: 'Status', accessorKey: 'status', cell: ({ getValue }) => <StatusBadge value={getValue<string>()} /> },
	{
		header: 'Redemptions',
		cell: ({ row }) => (
			<span className="tabular-nums">
				{row.original.redemptions ?? 0}
				{row.original.maxRedemptions ? ` / ${row.original.maxRedemptions}` : ''}
			</span>
		),
	},
	{
		header: '',
		id: 'link',
		cell: ({ row }) =>
			row.original.chargebeeUrl && (
				<a
					href={row.original.chargebeeUrl}
					target="_blank"
					rel="noreferrer"
					className="text-ink-faint hover:text-brand"
					aria-label="Open in Chargebee"
				>
					<ExternalLink className="h-4 w-4" />
				</a>
			),
	},
]

export function CouponsPage() {
	const queryClient = useQueryClient()
	// Chargebee pages with an opaque offset token, so going back means
	// remembering where we have been — there is no page number to jump to.
	const [cursors, setCursors] = useState<string[]>([])
	const [creating, setCreating] = useState(false)
	const cursor = cursors[cursors.length - 1]

	const { data, isPending, error, refetch } = useQuery({
		queryKey: ['coupons', cursor],
		queryFn: () => apiFetch<CouponList>(`/api/admin/coupons${qs({ cursor })}`),
	})

	return (
		<>
			<PageHeader
				title="Coupons"
				subtitle="Held in Chargebee — there is no local copy, so nothing can drift."
				actions={
					<button type="button" className="btn-primary" onClick={() => setCreating(true)}>
						<Plus className="h-4 w-4" /> New coupon
					</button>
				}
			/>

			<div className="card">
				{isPending ? (
					<Spinner />
				) : error ? (
					<ErrorState error={error} onRetry={refetch} />
				) : (
					<>
						<DataTable columns={columns} rows={data.rows} emptyMessage="No coupons on this Chargebee site." />
						<CursorPagination
							cursors={cursors}
							nextCursor={data.nextCursor}
							onBack={() => setCursors((c) => c.slice(0, -1))}
							onNext={() => data.nextCursor && setCursors((c) => [...c, data.nextCursor!])}
						/>
					</>
				)}
			</div>

			{creating && (
				<CreateCouponDialog
					onClose={() => setCreating(false)}
					onCreated={() => {
						setCreating(false)
						setCursors([])
						queryClient.invalidateQueries({ queryKey: ['coupons'] })
					}}
				/>
			)}
		</>
	)
}

function CreateCouponDialog({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
	const [form, setForm] = useState({
		id: '',
		name: '',
		discountType: 'percentage' as 'percentage' | 'fixed_amount',
		discountPercentage: '10',
		discountAmount: '',
		currencyCode: 'EUR',
		durationType: 'one_time' as 'one_time' | 'forever' | 'limited_period',
		maxRedemptions: '',
	})

	const create = useMutation({
		mutationFn: () =>
			apiFetch('/api/admin/coupons', {
				method: 'POST',
				body: {
					id: form.id.trim(),
					name: form.name.trim(),
					discountType: form.discountType,
					durationType: form.durationType,
					...(form.discountType === 'percentage'
						? { discountPercentage: Number(form.discountPercentage) }
						: // Entered in major units, sent in minor units like the rest of Chargebee.
							{ discountAmount: Math.round(Number(form.discountAmount) * 100), currencyCode: form.currencyCode }),
					...(form.maxRedemptions ? { maxRedemptions: Number(form.maxRedemptions) } : {}),
				},
			}),
		onSuccess: onCreated,
	})

	function set<K extends keyof typeof form>(key: K) {
		return (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
			setForm((f) => ({ ...f, [key]: e.target.value }))
	}

	return (
		<div className="fixed inset-0 z-50 flex items-center justify-center p-4">
			<button type="button" aria-hidden tabIndex={-1} className="absolute inset-0 bg-black/40" onClick={onClose} />
			<form
				role="dialog"
				aria-modal
				className="card relative z-10 w-full max-w-md p-5"
				onSubmit={(e) => {
					e.preventDefault()
					create.mutate()
				}}
			>
				<h2 className="mb-4 font-display text-lg font-semibold">New coupon</h2>

				<label className="label" htmlFor="code">Code</label>
				<input id="code" className="input mb-3 font-mono" value={form.id} onChange={set('id')} placeholder="SUMMER25" required />

				<label className="label" htmlFor="cname">Name</label>
				<input id="cname" className="input mb-3" value={form.name} onChange={set('name')} required />

				<div className="mb-3 grid grid-cols-2 gap-3">
					<div>
						<label className="label" htmlFor="dtype">Type</label>
						<select id="dtype" className="input" value={form.discountType} onChange={set('discountType')}>
							<option value="percentage">Percentage</option>
							<option value="fixed_amount">Fixed amount</option>
						</select>
					</div>
					{form.discountType === 'percentage' ? (
						<div>
							<label className="label" htmlFor="pct">Percent off</label>
							<input id="pct" type="number" min="1" max="100" className="input" value={form.discountPercentage} onChange={set('discountPercentage')} required />
						</div>
					) : (
						<div>
							<label className="label" htmlFor="amt">Amount</label>
							<input id="amt" type="number" min="0.01" step="0.01" className="input" value={form.discountAmount} onChange={set('discountAmount')} required />
						</div>
					)}
				</div>

				<div className="mb-3 grid grid-cols-2 gap-3">
					<div>
						<label className="label" htmlFor="dur">Duration</label>
						<select id="dur" className="input" value={form.durationType} onChange={set('durationType')}>
							<option value="one_time">One time</option>
							<option value="forever">Forever</option>
							<option value="limited_period">Limited period</option>
						</select>
					</div>
					<div>
						<label className="label" htmlFor="max">Max redemptions</label>
						<input id="max" type="number" min="1" className="input" value={form.maxRedemptions} onChange={set('maxRedemptions')} placeholder="unlimited" />
					</div>
				</div>

				{form.discountType === 'fixed_amount' && (
					<p className="mb-3 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-900">
						The customer checkout only supports percentage coupons in its price maths — a fixed-amount code will be
						rejected there.
					</p>
				)}

				{create.error && (
					<p role="alert" className="mb-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
						{(create.error as Error).message}
					</p>
				)}

				<div className="flex justify-end gap-2">
					<button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
					<button type="submit" className="btn-primary" disabled={create.isPending}>
						{create.isPending ? 'Creating…' : 'Create coupon'}
					</button>
				</div>
			</form>
		</div>
	)
}
